import type { ColorCieLab } from "service/color-math";
import type { NamedColor } from "./ColorFilter";

export const OEM_COLOR_CODE_SYSTEM = "oem_color_code";

export type OemColorMatch = "exact" | "similar";

/** Library list item of the `oemColorLibrary` autocomplete. */
export interface OemLibraryOption {
  name: string;
  value: string;
}

/** Code list item of the `oemColorCodeImagio` autocomplete; code, name and Lab may be missing. */
export interface OemColorCodeOption {
  groupName: string;
  value: string | null;
  name: string | null;
  cieLab: ColorCieLab | null;
}

/**
 * The OEM color pick of the color filter. `library` alone is a half-finished pick
 * (no search yet); `match` is kept even without a library so the checkbox survives.
 */
export interface OemColorValue {
  library?: string;
  code?: string;
  name?: string;
  cieLab?: ColorCieLab;
  match: OemColorMatch;
}

/** A search needs the library plus a code or a name. */
export const isOemColorSearchable = (
  oemColor: OemColorValue | undefined,
): oemColor is OemColorValue & { library: string } =>
  Boolean(oemColor?.library && (oemColor.code || oemColor.name));

/** Chip and ΔE target label: library / code / name. */
export const oemColorTitle = (oemColor: OemColorValue): string =>
  [oemColor.library, oemColor.code, oemColor.name].filter(Boolean).join(" / ");

/** The color results are compared against (ΔE) for an OEM pick; none without Lab. */
export const oemColorTarget = (
  oemColor: OemColorValue | undefined,
): NamedColor | undefined =>
  oemColor?.cieLab
    ? { value: oemColor.cieLab, name: oemColorTitle(oemColor) }
    : undefined;

/** A code or name was picked but the list has no Lab for it. */
export const isOemPickWithoutLab = (oemColor: OemColorValue): boolean =>
  Boolean(oemColor.code || oemColor.name) && !oemColor.cieLab;

/** Similar matching ranks by ΔE against the pick's Lab, so a pick without Lab can only match exactly. */
export const effectiveOemMatch = (oemColor: OemColorValue): OemColorMatch =>
  isOemPickWithoutLab(oemColor) ? "exact" : oemColor.match;
